import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  TextField,
  Grid
} from '@mui/material';
import {
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { authGet, authPost } from '../../services/apiService';

/**
 * Salesforce admin: connection test. Silently calls GET /status on load (and
 * after a successful test) just to pre-fill Instance URL from whatever the
 * server actually has configured, instead of always showing the hardcoded
 * sandbox default.
 */
const SalesforceConfigAdmin = () => {
  const [instanceUrl, setInstanceUrl] = useState('https://thomsonreutersglis2e--uat.sandbox.my.salesforce.com');
  const [query, setQuery] = useState("SELECT COUNT() FROM Case WHERE Status IN ('New', 'Open', 'Work In Progress') AND RecordType.DeveloperName IN ('ESC_Tech_SupportReadOnly', 'ESC_Case_HighQ_Intake')");
  const [reportId, setReportId] = useState('00OcZ000001b9jjUAA');
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionTestResult, setConnectionTestResult] = useState(null);
  const [reportRowCountSync, setReportRowCountSync] = useState(null);
  const [reportRowCountAsync, setReportRowCountAsync] = useState(null);

  // Dark green color matching the theme
  const darkGreenColor = '#1D4434';

  const loadStatus = useCallback(async () => {
    try {
      const response = await authGet('/api/salesforce/status');
      if (response?.instanceUrl) setInstanceUrl(response.instanceUrl);
      if (response?.reportId) setReportId(response.reportId);
    } catch (error) {
      // Non-blocking: Instance URL just stays at its current/default value.
      console.warn('[Salesforce] Failed to load status:', error.message || error);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setConnectionTestResult(null);
    setReportRowCountSync(null);
    setReportRowCountAsync(null);
    try {
      const response = await authPost('/api/salesforce/test-connection', {
        instanceUrl,
        query,
        reportId
      });
      setConnectionTestResult({
        success: response.success,
        message: response.message || 'Query executed.'
      });
      // Both the sync (GET ?includeDetails=true) and async (POST instances ->
      // poll GET instances/{id}) Report APIs ran server-side when a Report ID
      // was supplied; these are the row counts present in each response.
      if (reportId) {
        if (response.reportRowCountSync !== null && response.reportRowCountSync !== undefined) {
          setReportRowCountSync(response.reportRowCountSync);
        }
        if (response.reportRowCountAsync !== null && response.reportRowCountAsync !== undefined) {
          setReportRowCountAsync(response.reportRowCountAsync);
        }
      }
      if (response.success) loadStatus();
    } catch (error) {
      setConnectionTestResult({
        success: false,
        message: error.message || 'Failed to test Salesforce connection.'
      });
    } finally {
      setTestingConnection(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, color: darkGreenColor }}>
        Salesforce Integration
      </Typography>

      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SettingsIcon sx={{ mr: 1, color: darkGreenColor }} />
          <Typography variant="h6" sx={{ color: darkGreenColor }}>
            Salesforce Configuration
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Instance URL"
              value={instanceUrl}
              onChange={(e) => setInstanceUrl(e.target.value)}
              placeholder="https://yourinstance.salesforce.com"
              fullWidth
              required
              helperText="Your Salesforce instance URL"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Case Query"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="SELECT Id, Name FROM Account"
              fullWidth
              helperText="SOQL query to run against Salesforce"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Report ID"
              value={reportId}
              onChange={(e) => setReportId(e.target.value)}
              placeholder="00O..."
              fullWidth
              helperText="Salesforce Report ID"
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="outlined"
              startIcon={testingConnection ? <RefreshIcon /> : <CheckCircleIcon />}
              onClick={handleTestConnection}
              disabled={testingConnection || !instanceUrl || !query}
              sx={{
                borderColor: darkGreenColor,
                color: darkGreenColor,
                '&:hover': {
                  borderColor: darkGreenColor,
                  bgcolor: `${darkGreenColor}10`
                }
              }}
            >
              {testingConnection ? 'Testing...' : 'Test Connection'}
            </Button>
          </Grid>
          {connectionTestResult && (
            <Grid item xs={12}>
              <Alert
                severity={connectionTestResult.success ? 'success' : 'info'}
                onClose={() => setConnectionTestResult(null)}
              >
                {connectionTestResult.message}
              </Alert>
            </Grid>
          )}
          {reportRowCountSync !== null && (
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ color: darkGreenColor, fontWeight: 600 }}>
                Sync report row count: {reportRowCountSync}
              </Typography>
            </Grid>
          )}
          {reportRowCountAsync !== null && (
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ color: darkGreenColor, fontWeight: 600 }}>
                Async report row count: {reportRowCountAsync}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>
    </Box>
  );
};

export default SalesforceConfigAdmin;
